<!--
  采购实例详情页
  用途：从 API 获取案例详情，展示挑战/方案/成果与媒体
  用法：路由 `/cases/:id`
-->
<template>
  <div class="case-detail-page">
    <p v-if="loading" class="state">{{ t('common.loading') }}</p>
    <p v-else-if="error" class="state">{{ error }}</p>

    <div v-else-if="caseItem" class="detail-inner">
      <button class="back-btn" type="button" @click="goBack">{{ t('cases.backList') }}</button>

      <div class="detail-hero">
        <MediaGallery
          class="ratio-wide"
          :items="caseItem.gallery"
          :fallback="caseItem.cover"
          :alt="caseItem.title"
        />
      </div>

      <div class="detail-main">
        <div class="detail-meta">
          <span class="category">{{ caseItem.category }}</span>
          <span class="year">{{ caseItem.year }}</span>
        </div>
        <h1>{{ caseItem.title }}</h1>
        <p class="client">{{ caseItem.client }}</p>
        <p class="summary">{{ caseItem.summary }}</p>

        <div class="metrics">
          <div>
            <strong>{{ caseItem.metrics.saving }}</strong>
            <span>{{ caseItem.metricLabels.saving }}</span>
          </div>
          <div>
            <strong>{{ caseItem.metrics.units }}</strong>
            <span>{{ caseItem.metricLabels.units }}</span>
          </div>
          <div>
            <strong>{{ caseItem.metrics.leadTime }}</strong>
            <span>{{ caseItem.metricLabels.leadTime }}</span>
          </div>
        </div>

        <section class="block">
          <h2>{{ t('cases.challenge') }}</h2>
          <p>{{ caseItem.challenge }}</p>
        </section>
        <section class="block">
          <h2>{{ t('cases.solution') }}</h2>
          <p>{{ caseItem.solution }}</p>
        </section>
        <section class="block">
          <h2>{{ t('cases.result') }}</h2>
          <p>{{ caseItem.result }}</p>
        </section>

        <section class="block">
          <h2>{{ t('cases.highlights') }}</h2>
          <ul class="highlights">
            <li v-for="(point, index) in caseItem.highlights" :key="index">{{ point }}</li>
          </ul>
        </section>

        <div class="actions">
          <router-link class="btn btn-primary" to="/contact">{{ t('cases.cta') }}</router-link>
          <router-link class="btn btn-ghost" to="/cases">{{ t('cases.moreCases') }}</router-link>
        </div>
      </div>
    </div>

    <div v-else class="empty">
      <img class="empty-logo" :src="logoUrl" :alt="t('common.brandLogoAlt')" />
      <h2>{{ t('cases.notFoundTitle') }}</h2>
      <p>{{ t('cases.notFoundDesc') }}</p>
      <router-link class="btn btn-primary" to="/cases">{{ t('cases.backList') }}</router-link>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import MediaGallery from '@/components/MediaGallery.vue'
import { fetchCase, fetchCategories } from '@/services/api'
import { localizeCaseFromApi } from '@/utils/localize'
import logoUrl from '@/assets/logo.png'

const route = useRoute()
const router = useRouter()
const { t, locale } = useI18n()

const raw = ref(null)
const categories = ref([])
const loading = ref(true)
const error = ref('')

const caseItem = computed(() =>
  localizeCaseFromApi(raw.value, locale.value, t, categories.value)
)

const load = async () => {
  loading.value = true
  error.value = ''
  raw.value = null
  try {
    const [caseData, categoryData] = await Promise.all([
      fetchCase(route.params.id),
      fetchCategories()
    ])
    raw.value = caseData
    categories.value = categoryData
  } catch (e) {
    if (e.status !== 404) error.value = e.message || t('common.loadFailed')
  } finally {
    loading.value = false
  }
}

watch(() => route.params.id, load, { immediate: true })

const goBack = () => {
  if (window.history.length > 1) router.back()
  else router.push('/cases')
}
</script>

<style scoped>
.case-detail-page {
  max-width: var(--max-width);
  margin: 0 auto;
  padding: 28px 24px 64px;
}

.state {
  padding: 48px 16px;
  text-align: center;
  color: var(--color-text-secondary);
}

.back-btn {
  background: transparent;
  color: var(--color-text-secondary);
  font-size: 14px;
  margin-bottom: 20px;
  cursor: pointer;
}

.back-btn:hover {
  color: var(--color-primary);
}

.detail-hero {
  margin-bottom: 28px;
}

.detail-main {
  text-align: left;
  max-width: 860px;
}

.detail-meta {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}

.category {
  padding: 4px 10px;
  border-radius: 999px;
  background: var(--color-primary-soft);
  color: var(--color-primary);
  font-size: 12px;
  font-weight: 600;
}

.year {
  font-size: 13px;
  color: var(--color-text-secondary);
}

.detail-main h1 {
  font-size: clamp(26px, 3vw, 36px);
  font-weight: 700;
  line-height: 1.3;
  margin-bottom: 8px;
}

.client {
  font-size: 14px;
  color: var(--color-text-secondary);
  margin-bottom: 14px;
}

.summary {
  font-size: 16px;
  line-height: 1.75;
  color: var(--color-text);
  margin-bottom: 22px;
}

.metrics {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-bottom: 32px;
}

.metrics > div {
  padding: 16px;
  border-radius: 12px;
  background: var(--color-bg-muted);
  text-align: center;
}

.metrics strong {
  display: block;
  font-size: 24px;
  font-weight: 700;
  color: var(--color-primary);
  margin-bottom: 4px;
}

.metrics span {
  font-size: 13px;
  color: var(--color-text-secondary);
}

.block {
  margin-bottom: 24px;
}

.block h2 {
  font-size: 18px;
  font-weight: 700;
  margin-bottom: 8px;
}

.block p {
  color: var(--color-text-secondary);
  line-height: 1.75;
  font-size: 15px;
}

.highlights {
  display: grid;
  gap: 10px;
}

.highlights li {
  position: relative;
  padding-left: 18px;
  color: var(--color-text-secondary);
  font-size: 15px;
  line-height: 1.6;
}

.highlights li::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0.55em;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--color-primary);
}

.actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 28px;
}

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 128px;
  padding: 12px 20px;
  border-radius: 999px;
  font-size: 14px;
  font-weight: 600;
}

.btn-primary {
  background: var(--color-primary);
  color: #fff;
}

.btn-ghost {
  background: #fff;
  color: var(--color-text);
  border: 1px solid var(--color-border);
}

.empty {
  text-align: center;
  padding: 80px 16px;
}

.empty-logo {
  width: 56px;
  height: 56px;
  margin-bottom: 16px;
}

@media (max-width: 640px) {
  .case-detail-page {
    padding: 20px 16px 48px;
  }

  .metrics {
    grid-template-columns: 1fr;
  }
}
</style>
